import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import toast from "react-hot-toast";
import {
  Plus,
  Search,
  Library,
  FolderKanban,
  MoreHorizontal,
  Pencil,
  Trash2,
  X,
  LogOut,
  ChevronUp,
  Settings,
  PanelLeftClose,
  PanelLeft,
  MessageSquare,
  BookOpen,
  Shield,
} from "lucide-react";
import { useSidebarStore } from "../../../stores/sidebarStore";
import { useChatStore } from "../../../stores/chatStore";
import { conversationService } from "../../../services/conversationService";
import usageService from "../../../services/usageService";
import { groupConversationsByDate, getGroupLabel } from "../../../utils/dateGrouping";
import { authService } from "../../auth";
import { disconnectLabs } from "../../../services/labsWebSocket";
import SettingsModal from "../../settings/SettingsModal";

// Conversation Item Component
const ConversationItem = ({ conversation, isActive, onSelect, onRename, onDelete, onPrefetch }) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [title, setTitle] = React.useState(conversation.title);
  const [showMenu, setShowMenu] = React.useState(false);
  const inputRef = React.useRef(null);
  const menuRef = React.useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowMenu(false);
        setIsEditing(false);
        setTitle(conversation.title);
      }
    };
    if (showMenu || isEditing) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showMenu, isEditing, conversation.title]);

  const handleRename = () => {
    if (title.trim() && title !== conversation.title) {
      onRename(conversation.id, title.trim());
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleRename();
    } else if (e.key === "Escape") {
      setTitle(conversation.title);
      setIsEditing(false);
    }
  };

  return (
    <div
      className={`group relative flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
        isActive
          ? "bg-sedaily-500/10 text-text-100"
          : "hover:bg-bg-300/30 text-text-300 hover:text-text-200"
      }`}
      onClick={() => !isEditing && onSelect(conversation.id)}
      onMouseEnter={() => onPrefetch?.(conversation.id)}
      onMouseLeave={() => setShowMenu(false)}
    >
      {/* Title */}
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={handleRename}
          onKeyDown={handleKeyDown}
          className="flex-1 bg-bg-300 text-text-100 text-sm px-2 py-1 rounded outline-none focus:ring-1 focus:ring-sedaily-500/30"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span className="flex-1 text-sm truncate">{conversation.title}</span>
      )}

      {/* Menu Button */}
      {!isEditing && (
        <div className="opacity-0 group-hover:opacity-100 transition-opacity" ref={menuRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="p-1 rounded hover:bg-bg-400/50"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>

          {/* Dropdown Menu */}
          <AnimatePresence>
            {showMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="absolute right-0 top-full mt-1 bg-bg-100 rounded-lg z-50 py-1 min-w-[120px] shadow-lg border border-bg-300/30"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => {
                    setIsEditing(true);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text-300 hover:bg-bg-300 hover:text-text-100"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Rename
                </button>
                <button
                  onClick={() => {
                    onDelete(conversation.id);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

// Conversation Group Component
const ConversationGroup = ({ title, conversations, currentId, onSelect, onRename, onDelete, onPrefetch }) => {
  if (conversations.length === 0) return null;

  return (
    <div className="mb-3">
      <h3 className="text-[11px] font-medium text-text-500 uppercase tracking-wider px-3 mb-1">
        {title}
      </h3>
      <div className="space-y-0.5">
        {conversations.map((conv) => (
          <ConversationItem
            key={conv.id}
            conversation={conv}
            isActive={conv.id === currentId}
            onSelect={onSelect}
            onRename={onRename}
            onDelete={onDelete}
            onPrefetch={onPrefetch}
          />
        ))}
      </div>
    </div>
  );
};

// User Profile Section Component (Expanded)
const UserProfileSection = ({ navigate }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [usageInfo, setUsageInfo] = useState(null);
  const menuRef = React.useRef(null);
  const { setConversations } = useSidebarStore();

  useEffect(() => {
    const storedUserInfo = localStorage.getItem('userInfo');
    if (storedUserInfo) {
      try {
        setUserInfo(JSON.parse(storedUserInfo));
      } catch (e) {
        console.error('Failed to parse user info');
      }
    }
  }, []);

  useEffect(() => {
    if (showMenu) {
      usageService.getMyUsage()
        .then((res) => setUsageInfo(res.data))
        .catch(() => {});
    }
  }, [showMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const handleLogout = async () => {
    try {
      await authService.signOut();
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('authToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userRole');
      setConversations([]);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const getUserInitial = () => {
    if (userInfo?.email) return userInfo.email.charAt(0).toUpperCase();
    if (userInfo?.name) return userInfo.name.charAt(0).toUpperCase();
    return 'U';
  };

  const getUserDisplayName = () => {
    if (userInfo?.name) return userInfo.name;
    if (userInfo?.email) return userInfo.email.split('@')[0];
    return 'User';
  };

  const getUserEmail = () => userInfo?.email || '';

  const getUserTier = () => userInfo?.tier || 'free';

  const tierConfig = {
    admin: { label: 'Admin', color: 'bg-red-500/20 text-red-400' },
    enterprise: { label: 'Enterprise', color: 'bg-purple-500/20 text-purple-400' },
    pro: { label: 'Pro', color: 'bg-blue-500/20 text-blue-400' },
    free: { label: 'Free', color: 'bg-gray-500/20 text-gray-400' },
  };

  return (
    <div className="p-2">
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-bg-300/40 transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-sedaily-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
            {getUserInitial()}
          </div>
          <div className="flex-1 text-left min-w-0">
            <div className="text-sm font-medium text-text-200 truncate">{getUserDisplayName()}</div>
            <div className="text-xs text-text-500 truncate">{getUserEmail()}</div>
          </div>
          <ChevronUp className={`w-4 h-4 text-text-400 transition-transform ${showMenu ? '' : 'rotate-180'}`} />
        </button>

        <AnimatePresence>
          {showMenu && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              className="absolute bottom-full left-0 right-0 mb-1 bg-bg-100 rounded-lg overflow-hidden z-50 shadow-lg border border-bg-300/30"
            >
              <div className="px-3 py-2.5">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-text-500">Plan</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tierConfig[usageInfo?.tier || getUserTier()]?.color || tierConfig.free.color}`}>
                    {tierConfig[usageInfo?.tier || getUserTier()]?.label || 'Free'}
                  </span>
                </div>
                {usageInfo && usageInfo.limit > 0 && (
                  <>
                    <div className="w-full h-1.5 bg-bg-300/50 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          usageInfo.percentage >= 100 ? 'bg-red-500' :
                          usageInfo.percentage >= 80 ? 'bg-yellow-500' : 'bg-sedaily-500'
                        }`}
                        style={{ width: `${Math.min(usageInfo.percentage, 100)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-text-500">
                        {usageInfo.monthlyTokens >= 1000
                          ? `${(usageInfo.monthlyTokens / 1000).toFixed(1)}K`
                          : usageInfo.monthlyTokens}
                      </span>
                      <span className="text-[10px] text-text-500">
                        {usageInfo.limit >= 1_000_000
                          ? `${(usageInfo.limit / 1_000_000).toFixed(0)}M`
                          : `${(usageInfo.limit / 1000).toFixed(0)}K`}
                      </span>
                    </div>
                  </>
                )}
                {usageInfo && usageInfo.limit === -1 && (
                  <div className="text-[10px] text-text-500 mt-0.5">Unlimited</div>
                )}
              </div>
              <div className="h-px bg-bg-300/30" />
              <button
                onClick={() => {
                  setShowMenu(false);
                  setShowSettings(true);
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-text-300 hover:bg-bg-300/50 hover:text-text-100 transition-colors"
              >
                <Settings className="w-4 h-4" />
                Settings
              </button>
              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate("/guide");
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-text-300 hover:bg-bg-300/50 hover:text-text-100 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                Service Guide
              </button>
              {localStorage.getItem('userRole') === 'admin' && (
                <button
                  onClick={() => {
                    setShowMenu(false);
                    navigate("/admin");
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-text-300 hover:bg-bg-300/50 hover:text-text-100 transition-colors"
                >
                  <Shield className="w-4 h-4" />
                  Admin
                </button>
              )}
              <div className="h-px bg-bg-300/30" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Log out
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} />
    </div>
  );
};

// Main Sidebar Component
const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    conversations,
    setConversations,
    searchQuery,
    setSearchQuery,
    isCollapsed,
    toggleSidebar,
    updateConversationTitle,
    deleteConversation,
    isLoading,
    setLoading,
  } = useSidebarStore();

  const { currentConversationId, setCurrentConversation, resetChat, setLoadingConversation } = useChatStore();

  const loadConversations = React.useCallback(async () => {
    // 로그인 상태가 아니면 스킵
    if (!localStorage.getItem('isLoggedIn')) return;
    setLoading(true);
    try {
      const data = await conversationService.getConversations();
      console.log('[Sidebar] Loaded conversations:', data.length, 'items');
      setConversations(data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [setConversations, setLoading]);

  useEffect(() => {
    loadConversations();

    // 로그인 후 userId가 준비되면 대화 목록 재로드
    window.addEventListener('userInfoUpdated', loadConversations);
    // 새 대화 저장 완료 후 목록 갱신
    window.addEventListener('conversationSaved', loadConversations);
    return () => {
      window.removeEventListener('userInfoUpdated', loadConversations);
      window.removeEventListener('conversationSaved', loadConversations);
    };
  }, [loadConversations]);

  const filteredConversations = searchQuery
    ? conversations.filter(
        (c) =>
          c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.messages?.some((m) =>
            m.content.toLowerCase().includes(searchQuery.toLowerCase())
          )
      )
    : conversations;

  const groupedConversations = groupConversationsByDate(filteredConversations);

  const handleNewChat = () => {
    // 🔧 Disconnect WebSocket if streaming
    disconnectLabs();
    resetChat();
    setSearchQuery(''); // 검색어 초기화
    navigate("/");
  };

  // Prefetch: preload conversation on hover (save to cache)
  const prefetchedIds = React.useRef(new Set());

  const handlePrefetch = async (id) => {
    // Skip if already prefetched or current conversation
    if (prefetchedIds.current.has(id) || id === currentConversationId) return;

    // Skip if messages already in cache
    const cached = conversations.find(c => c.id === id);
    if (cached?.messages?.length > 0) return;

    prefetchedIds.current.add(id);

    try {
      const conv = await conversationService.getConversationById(id);
      if (conv?.messages?.length > 0) {
        // Cache messages in sidebarStore
        const { updateConversationMessages } = useSidebarStore.getState();
        updateConversationMessages(id, conv.messages);
        console.log('%c[PREFETCH] ✅ Conversation preloaded', 'color: #22c55e;', id);
      }
    } catch (error) {
      console.warn('[PREFETCH] Failed:', id, error);
      prefetchedIds.current.delete(id); // Allow retry
    }
  };

  const handleSelectConversation = (id) => {
    if (id === currentConversationId) return;

    // 🎬 Always show skeleton animation
    console.log('%c[SIDEBAR] 🚀 Conversation selected, skeleton ON', 'color: #3b82f6; font-weight: bold;');
    setLoadingConversation(true);
    setCurrentConversation(id);
    navigate(`/chat/${id}`);
  };

  const handleRename = async (id, newTitle) => {
    try {
      await conversationService.updateTitle(id, newTitle);
      updateConversationTitle(id, newTitle);
    } catch (error) {
      console.error("Failed to rename conversation:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await conversationService.deleteConversation(id);
      deleteConversation(id);
      if (currentConversationId === id) {
        resetChat();
        navigate("/");
      }
    } catch (error) {
      console.error("Failed to delete conversation:", error);
    }
  };

  return (
    <aside
      className={`h-full bg-bg-200/30 flex flex-col border-r border-bg-300/20 transition-all duration-300 ease-out overflow-hidden ${
        isCollapsed ? 'w-[52px]' : 'w-[260px]'
      }`}
    >
      {/* Header - 접힌 상태와 펼친 상태 공통 */}
      <div className={`flex items-center transition-all duration-300 ${isCollapsed ? 'flex-col py-2 px-0' : 'flex-row p-3 justify-between'}`}>
        {/* Logo - 펼친 상태에서만 표시 */}
        {!isCollapsed && (
          <span
            className="text-base font-semibold text-text-100 cursor-pointer hover:text-text-200 transition-colors"
            onClick={() => { resetChat(); navigate("/"); }}
          >AI Labs</span>
        )}
        <button
          onClick={toggleSidebar}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-text-400 hover:text-text-200 hover:bg-bg-300/50 transition-colors flex-shrink-0"
          title={isCollapsed ? "Open sidebar" : "Close sidebar"}
        >
          {isCollapsed ? <PanelLeft className="w-5 h-5" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
      </div>

      {/* New Chat Button */}
      <div className={`transition-all duration-300 ${isCollapsed ? 'px-1.5 pb-2' : 'px-3 pb-2'}`}>
        <button
          onClick={handleNewChat}
          className={`flex items-center rounded-lg bg-sedaily-500 text-white hover:bg-sedaily-600 transition-all duration-300 ${
            isCollapsed
              ? 'w-9 h-9 justify-center mx-auto'
              : 'w-full gap-2 px-3 py-2 text-sm'
          }`}
          title="New chat"
        >
          <Plus className={`flex-shrink-0 ${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'}`} />
          <span className={`whitespace-nowrap transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0 overflow-hidden' : 'w-auto opacity-100'}`}>
            New chat
          </span>
        </button>
      </div>

      {/* Search */}
      <div className={`transition-all duration-300 ${isCollapsed ? 'px-1.5 pb-2' : 'px-3 pb-3'}`}>
        {isCollapsed ? (
          <button
            onClick={toggleSidebar}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-text-400 hover:text-text-200 hover:bg-bg-300/50 transition-colors mx-auto"
            title="Search"
          >
            <Search className="w-4 h-4" />
          </button>
        ) : (
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-500" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-bg-300/30 text-text-200 text-sm pl-8 pr-3 py-1.5 rounded-lg outline-none focus:ring-1 focus:ring-sedaily-500/30 transition-colors placeholder:text-text-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-bg-400/50"
              >
                <X className="w-3 h-3 text-text-400" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Conversation List - 접힌 상태에서는 숨김 */}
      <div className={`flex-1 overflow-y-auto overflow-x-hidden transition-all duration-300 ${isCollapsed ? 'opacity-0 px-0' : 'opacity-100 px-2'}`}>
        {!isCollapsed && (
          <>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-sedaily-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="text-center py-8 text-text-500 text-sm">
                {searchQuery ? "No results" : "No conversations"}
              </div>
            ) : (
              <>
                <ConversationGroup
                  title={getGroupLabel("today")}
                  conversations={groupedConversations.today}
                  currentId={currentConversationId}
                  onSelect={handleSelectConversation}
                  onRename={handleRename}
                  onDelete={handleDelete}
                  onPrefetch={handlePrefetch}
                />
                <ConversationGroup
                  title={getGroupLabel("yesterday")}
                  conversations={groupedConversations.yesterday}
                  currentId={currentConversationId}
                  onSelect={handleSelectConversation}
                  onRename={handleRename}
                  onDelete={handleDelete}
                  onPrefetch={handlePrefetch}
                />
                <ConversationGroup
                  title={getGroupLabel("lastWeek")}
                  conversations={groupedConversations.lastWeek}
                  currentId={currentConversationId}
                  onSelect={handleSelectConversation}
                  onRename={handleRename}
                  onDelete={handleDelete}
                  onPrefetch={handlePrefetch}
                />
                <ConversationGroup
                  title={getGroupLabel("older")}
                  conversations={groupedConversations.older}
                  currentId={currentConversationId}
                  onSelect={handleSelectConversation}
                  onRename={handleRename}
                  onDelete={handleDelete}
                  onPrefetch={handlePrefetch}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* Bottom Section */}
      <div className="border-t border-bg-300/20">
        {/* Projects */}
        <div className={`pt-2 flex flex-col gap-1 transition-all duration-300 ${isCollapsed ? 'px-1.5' : 'px-2'}`}>
          <button
            onClick={() => toast("Coming Soon", { icon: "🚀" })}
            className={`flex items-center rounded-lg transition-all duration-300 ${
              isCollapsed
                ? 'w-9 h-9 justify-center mx-auto'
                : 'w-full gap-2.5 px-3 py-2 text-sm'
            } text-text-400 hover:text-text-200 hover:bg-bg-300/50`}
            title="Projects"
          >
            <FolderKanban className="w-4 h-4 flex-shrink-0" />
            <span className={`whitespace-nowrap transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0 overflow-hidden' : 'w-auto opacity-100'}`}>
              Projects
            </span>
          </button>
        </div>

        {/* User Profile */}
        {isCollapsed ? (
          <div className="p-2 flex justify-center">
            <button
              className="w-9 h-9 rounded-full bg-sedaily-500 flex items-center justify-center text-white text-sm font-semibold hover:bg-sedaily-600 transition-colors"
              title="Profile"
            >
              U
            </button>
          </div>
        ) : (
          <UserProfileSection navigate={navigate} />
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
